import React from 'react';

import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';

import {convertToCSSRules} from '#core/utils/utils.js';
import {getConfigByContext} from '#core/utils/config-utils.js';

import {COLOR, FONT} from '../../config/theme.js';

const dispatch = MojitoCore.Services.redux.store.dispatch;
const IntentActions = MojitoCore.Intents.actions;
const Button = MojitoPresentation.Components.Button;

const NavigationPathBuilder = MojitoPresentation.Base.Navigation.NavigationPathBuilder;

export default class BadRoutePageView extends MojitoCore.Presentation.UIViewImplementation {
    onHomeLinkClick() {
        dispatch(
            IntentActions.publishIntent(MojitoPresentation.Base.Intent.Types.VIEW, NavigationPathBuilder.toHome())
        );
    }

    render() {
        return (
            <div style={this.style.wrapper}>
                <div style={this.style.container}>
                    <div style={this.style.infoContainer}>
                        <div className={'ta-title'} style={this.style.title}>
                            {this.resolveString(`$DBX.BAD_ROUTE_PAGE_VIEW.ERROR_CODE`)}
                        </div>

                        <div className={'ta-oopsText'} style={this.style.oopsText}>
                            {this.resolveString(`$DBX.BAD_ROUTE_PAGE_VIEW.HEADER_OOOPS`)}
                        </div>

                        <div className={'ta-subTitle'} style={this.style.subTitle}>
                            {this.resolveString(`$DBX.BAD_ROUTE_PAGE_VIEW.TITLE`)}
                        </div>

                        <div className={'ta-infoText'} style={this.style.infoText}>
                            {this.resolveString(`$DBX.BAD_ROUTE_PAGE_VIEW.INFORMATION_FIRST_PART`)}{' '}
                            <a
                                href={`mailto:${this.resolveString('$DBX.BAD_ROUTE_PAGE_VIEW.INFORMATION_FIRST_PART_EMAIL')}`}
                                style={this.style.link}
                            >
                                {this.resolveString(`$DBX.BAD_ROUTE_PAGE_VIEW.INFORMATION_FIRST_PART_EMAIL`)}{' '}
                            </a>
                            {this.resolveString(`$DBX.BAD_ROUTE_PAGE_VIEW.INFORMATION_SECOND_PART`)}{' '}
                            <a
                                href={`tel:${this.resolveString('$DBX.BAD_ROUTE_PAGE_VIEW.INFORMATION_SECOND_PART_TELEPHONE')}`}
                                style={this.style.link}
                            >
                                {this.resolveString(`$DBX.BAD_ROUTE_PAGE_VIEW.INFORMATION_SECOND_PART_TELEPHONE`)}
                            </a>
                            .
                        </div>

                        <Button config={this.style.homeButton} onClick={this.onHomeLinkClick}>
                            <span style={this.style.homeButtonText}>
                                {this.resolveString(`$DBX.BAD_ROUTE_PAGE_VIEW.CONTACT_US`)}
                            </span>
                        </Button>
                    </div>

                    <div style={this.style.cornerError} />
                </div>

                <style
                    dangerouslySetInnerHTML={{
                        __html: `#appView { ${convertToCSSRules(this.style.app)} }`,
                    }}
                />
                <style dangerouslySetInnerHTML={{__html: `main { ${convertToCSSRules(this.style.main)} }`}} />
                <style
                    dangerouslySetInnerHTML={{__html: `.ta-sideMenu { ${convertToCSSRules(this.style.sideMenu)} }`}}
                />
                <style dangerouslySetInnerHTML={{__html: `footer { ${convertToCSSRules(this.style.footer)} }`}} />
                <style
                    dangerouslySetInnerHTML={{
                        __html: `footer > div:first-child > div:first-child > div:first-child { ${convertToCSSRules(
                            this.style.footerInner
                        )} }`,
                    }}
                />
            </div>
        );
    }
}

BadRoutePageView.getStyle = (config, applicationMode, merge) => {
    return getConfigByContext(
        {
            app: {
                _desktop_backgroundColor: `${COLOR.BRAND.BACKGROUND1}!important`,
            },
            container: merge(config.container.style, {
                display: 'flex',
                flexDirection: 'column',
                fontSize: '50px',
                _mobile_fontSize: '22px',
                padding: '32px 24px',
                _desktop_padding: '72px 144px 164px',
                // prevent overlapping inner content with sideMenu element
                _desktop_position: 'relative',
                _desktop_zIndex: 1,
                backgroundColor: COLOR.BRAND.BACKGROUND1,
            }),
            cornerError: {
                display: 'flex',
                alignSelf: 'center',
                _desktop_alignSelf: 'flex-end',
                width: '312px',
                _desktop_width: '530px',
                height: '200px',
                _desktop_height: '340px',
                backgroundImage: 'url(/images/bad-route/bad-route.svg)',
                backgroundSize: '100% 100%',
                margin: '56px auto 0',
                _desktop_margin: '-164px 0 -164px 0',
            },
            footer: {
                borderTopColor: COLOR.BRAND.FOREGROUND5,
                borderTopStyle: 'solid',
                borderTopWidth: '8px',
                _desktop_borderTopWidth: '48px',
            },
            footerInner: {
                _desktop_marginTop: '0!important',
            },
            homeButton: merge(config.homeButton, {
                style: {
                    base: {
                        flex: 'none',
                        margin: '16px 0 0',
                        _desktop_margin: '24px 0 0 0',
                        width: '126px',
                        _desktop_width: '265px',
                    },
                },
            }),
            homeButtonText: {
                color: COLOR.BRAND.BACKGROUND6,
                fontSize: '12px',
                _desktop_fontSize: '20px',
                fontWeight: FONT.WEIGHT_SEMI_BOLD,
            },
            infoContainer: {
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'normal',
                alignItems: 'normal',
                textAlign: 'start',
                margin: 0,
                _mobile_margin: 0,
                // prevent overlapping inner content with cornerError element
                _desktop_position: 'relative',
                _desktop_zIndex: 1,
            },
            infoText: {
                fontSize: '20px',
                _mobile_fontSize: '12px',
                color: COLOR.BRAND.BACKGROUND6,
                fontWeight: 500,
                lineHeight: '16px',
                _desktop_lineHeight: '25px',
                margin: 0,
                maxWidth: 'auto',
                _desktop_maxWidth: '666px',
            },
            link: {
                color: COLOR.BRAND.CTA2,
                textDecoration: 'none',
            },
            main: {
                _mobile_marginBottom: '0!important',
            },
            oopsText: {
                color: COLOR.BRAND.ACCENT3,
                fontSize: '28px',
                _desktop_fontSize: '68px',
                fontWeight: FONT.WEIGHT_SEMI_BOLD,

                lineHeight: '28px',
                _desktop_lineHeight: '77px',
                textTransform: 'uppercase',
            },
            sideMenu: {
                _desktop_display: 'none!important',
            },
            subTitle: {
                color: COLOR.BRAND.FOREGROUND2,
                fontSize: '14px',
                _desktop_fontSize: '34px',
                fontWeight: FONT.WEIGHT_SEMI_BOLD,

                margin: '8px 0',
                _desktop_margin: '24px 0',
                maxWidth: 'auto',
                _desktop_maxWidth: '441px',
            },
            title: {
                color: COLOR.BRAND.BACKGROUND6,
                fontSize: '24px',
                fontWeight: FONT.WEIGHT_SEMI_BOLD,

                lineHeight: '22px',
                _desktop_lineHeight: '46px',
                textTransform: 'uppercase',
                _mobile_marginBottom: 10,
            },
            wrapper: {
                width: '100%',
            },
        },
        applicationMode
    );
};
